/**
 * Property Level Service
 * 
 * Service for managing the 5-level property system:
 * - Level 1: Global Properties (enhances GlobalPropertiesService)
 * - Level 2: Global Property Templates
 * - Level 3: Prime Artifact Properties
 * - Level 4: Company Artifact Properties
 * - Level 5: Artifact Item Property Values
 * 
 * @see docs/api/FRONTEND_UI_REQUIREMENTS.md for complete specification
 */

import { apiClient } from '../apiClient';
import {
  GlobalPropertyDefinition,
  GlobalPropertyTemplate,
  TemplatePropertyMapping,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  AddPropertyToTemplateRequest,
  ApplyTemplateResult,
  PrimeArtifactProperty,
  UpdatePrimePropertyRequest,
  CompanyArtifactProperty,
  ResolvedPropertyConfig,
  ArtifactItemProperties,
  UpdateArtifactItemPropertiesRequest,
  RecalculatePropertyResponse,
  PropertyFilters,
  TemplateFilters,
  GlobalPropertyTemplatesResponse,
  PrimePropertiesResponse,
  CompanyPropertiesResponse
} from '../../types/propertyLevel';

// ============================================================================
// API Endpoints
// ============================================================================

const API_ENDPOINTS = {
  // Level 1: Global Properties
  GLOBAL_PROPERTIES: '/properties',
  GLOBAL_PROPERTY: (propertyKey: string) => `/properties/${encodeURIComponent(propertyKey)}`,
  
  // Level 2: Property Templates (using state-templates backend - repurposed as global property templates)
  TEMPLATES: '/state-templates',
  TEMPLATE: (templateId: string) => `/state-templates/${templateId}`,
  TEMPLATE_PROPERTY_MAPPINGS: (templateId: string) => `/state-templates/${templateId}/property-mappings`,
  TEMPLATE_PROPERTY_MAPPING: (templateId: string, propertyKey: string) => 
    `/state-templates/${templateId}/property-mappings/${encodeURIComponent(propertyKey)}`,
  // Legacy endpoint path (kept for backward compatibility if needed)
  TEMPLATE_PROPERTIES: (templateId: string) => `/state-templates/${templateId}/properties`,
  TEMPLATE_PROPERTY: (templateId: string, propertyKey: string) => 
    `/state-templates/${templateId}/properties/${encodeURIComponent(propertyKey)}`,
  APPLY_TEMPLATE: (templateId: string, primeArtifactId: string) => 
    `/state-templates/${templateId}/apply/${primeArtifactId}`,
  
  // Level 3: Prime Artifact Properties
  PRIME_PROPERTIES: (primeArtifactId: string) => `/prime-artifacts/${primeArtifactId}/properties`,
  PRIME_PROPERTY: (primeArtifactId: string, propertyKey: string) => 
    `/prime-artifacts/${primeArtifactId}/properties/${encodeURIComponent(propertyKey)}`,
  
  // Level 4: Company Artifact Properties
  COMPANY_PROPERTIES: (artifactId: string) => `/artifacts/${artifactId}/properties`,
  COMPANY_PROPERTY: (artifactId: string, propertyKey: string) => 
    `/artifacts/${artifactId}/properties/${encodeURIComponent(propertyKey)}`,
  RESOLVED_CONFIG: (artifactId: string, propertyKey: string) => 
    `/artifacts/${artifactId}/properties/${encodeURIComponent(propertyKey)}/resolved`,
  
  // Level 5: Artifact Item Properties
  ARTIFACT_ITEM_PROPERTIES: (itemId: string) => `/artifact-items/${itemId}/properties`,
  ARTIFACT_ITEM_PROPERTY: (itemId: string, propertyKey: string) => 
    `/artifact-items/${itemId}/properties/${encodeURIComponent(propertyKey)}`,
  RECALCULATE_PROPERTY: (itemId: string, propertyKey: string) => 
    `/artifact-items/${itemId}/properties/${encodeURIComponent(propertyKey)}/recalculate`,
};

// ============================================================================
// Level 1: Global Properties (enhancement to existing service)
// ============================================================================

export class PropertyLevelService {
  // ============================================================================
  // Level 2: Property Templates
  // ============================================================================

  /**
   * Get all property templates
   * Note: Backend uses state-templates API which has been repurposed as property templates
   */
  static async getTemplates(filters?: TemplateFilters): Promise<GlobalPropertyTemplatesResponse> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.TEMPLATES, { params: filters });
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        const items = response.data.map((t: any) => this.transformTemplate(t));
        return {
          items,
          total: items.length,
          skip: filters?.skip || 0,
          limit: filters?.limit || 100
        };
      }
      
      return response.data;
    } catch (error: any) {
      console.error('[PropertyLevelService] Error fetching templates:', error);
      throw error;
    }
  }

  /**
   * Convert a name to a template_key format (snake_case, lowercase, alphanumeric + underscores)
   */
  private static nameToTemplateKey(name: string): string {
    if (!name) return '';
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_]+/g, '_')  // Replace non-alphanumeric with underscore
      .replace(/^_+|_+$/g, '')        // Remove leading/trailing underscores
      .replace(/_+/g, '_');           // Replace multiple underscores with single
  }

  /**
   * Check if a string is a valid UUID
   */
  private static isUUID(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }

  /**
   * Resolve template identifier (name, key, or UUID) to template UUID
   * @param templateIdentifier - Template name, key, or UUID
   * @returns Template UUID
   */
  private static async resolveTemplateId(templateIdentifier: string): Promise<string> {
    // If it's already a UUID, return it
    if (this.isUUID(templateIdentifier)) {
      return templateIdentifier;
    }

    // Otherwise, resolve by fetching templates and matching
    const response = await this.getTemplates();
    const template = response.items.find(
      t => t.template_key === templateIdentifier || 
           t.template_name === templateIdentifier ||
           t.id === templateIdentifier
    );

    if (!template) {
      throw new Error(`Template '${templateIdentifier}' not found`);
    }

    return template.id;
  }

  /**
   * Transform backend state template format to frontend property template format
   */
  private static transformTemplate(backendTemplate: any): GlobalPropertyTemplate {
    // Generate template_key from name (backend doesn't store template_key separately)
    // Use name converted to snake_case, or fall back to id if name is missing
    const templateKey = backendTemplate.name 
      ? this.nameToTemplateKey(backendTemplate.name)
      : backendTemplate.id;
    
    return {
      id: backendTemplate.id,
      template_key: templateKey,
      template_name: backendTemplate.name,
      description: backendTemplate.description,
      artifact_type_hint: backendTemplate.applies_to_artifact_types?.[0] || undefined,
      metadata: {
        version: backendTemplate.version,
        tags: backendTemplate.tags,
        states: backendTemplate.states,
        transitions: backendTemplate.transitions,
      },
      properties: (() => {
        // Filter out deleted mappings first
        const validMappings = backendTemplate.property_mappings?.filter((mapping: any) => {
          if (mapping.is_deleted === true) {
            console.log(`[PropertyLevelService] Skipping deleted property mapping: ${mapping.property_key} (id: ${mapping.id})`);
            return false;
          }
          return true;
        }) || [];
        
        // Transform valid mappings
        const transformedMappings = validMappings.map((mapping: any, index: number): TemplatePropertyMapping => {
          // Ensure we have an ID - it's required for delete operations
          if (!mapping.id) {
            console.warn(`[PropertyLevelService] Property mapping missing ID:`, {
              property_key: mapping.property_key,
              mapping: mapping
            });
          }
          
          return {
            id: mapping.id || `temp-${mapping.property_key}-${index}`, // Fallback ID if missing (shouldn't happen)
            property_key: mapping.property_key,
            property_type: mapping.property_type,
            global_property_ref: mapping.global_option_catalog_ref || mapping.global_property_key,
            is_required: mapping.is_required || false,  // Default to false if not in backend
            display_order: mapping.display_order ?? mapping.group_order ?? index,  // Use group_order or index as fallback
            override_config: {
              created_in_states: mapping.created_in_states || [],
              updated_in_states: mapping.updated_in_states || [],
            },
          };
        });
        
        // Remove duplicates by property_key, keeping the first occurrence with an ID
        const seenKeys = new Map<string, TemplatePropertyMapping>();
        const uniqueMappings: TemplatePropertyMapping[] = [];
        
        for (const mapping of transformedMappings) {
          const existing = seenKeys.get(mapping.property_key);
          if (!existing) {
            seenKeys.set(mapping.property_key, mapping);
            uniqueMappings.push(mapping);
          } else {
            // If we find a duplicate, prefer the one with an ID
            if (mapping.id && !existing.id) {
              const index = uniqueMappings.indexOf(existing);
              uniqueMappings[index] = mapping;
              seenKeys.set(mapping.property_key, mapping);
            } else if (mapping.id && existing.id && mapping.id !== existing.id) {
              // Both have IDs - log a warning and keep the first
              console.warn(`[PropertyLevelService] Duplicate property mapping found for ${mapping.property_key}:`, {
                first_id: existing.id,
                second_id: mapping.id,
                keeping: existing.id
              });
            }
          }
        }
        
        if (uniqueMappings.length < transformedMappings.length) {
          console.log(`[PropertyLevelService] Removed ${transformedMappings.length - uniqueMappings.length} duplicate property mappings`);
        }
        
        return uniqueMappings;
      })(),
    };
  }

  /**
   * Get a specific property template
   * @param templateIdentifier - Template UUID, name, or key
   */
  static async getTemplate(templateIdentifier: string): Promise<GlobalPropertyTemplate> {
    try {
      // Resolve template identifier to UUID
      const templateId = await this.resolveTemplateId(templateIdentifier);
      
      console.log(`[PropertyLevelService] ===== getTemplate START =====`);
      console.log(`[PropertyLevelService] Template Identifier: ${templateIdentifier}, Resolved UUID: ${templateId}`);
      
      const response = await apiClient.get(API_ENDPOINTS.TEMPLATE(templateId));
      
      console.log(`[PropertyLevelService] Raw backend response:`, {
        id: response.data.id,
        name: response.data.name,
        property_mappings_count: response.data.property_mappings?.length || 0,
        property_mappings: response.data.property_mappings?.map((m: any) => ({
          id: m.id,
          id_type: typeof m.id,
          id_string: String(m.id),
          property_key: m.property_key,
          property_type: m.property_type,
          has_id: m.id !== undefined && m.id !== null
        })) || [],
        first_mapping_sample: response.data.property_mappings?.[0] ? {
          keys: Object.keys(response.data.property_mappings[0]),
          full: response.data.property_mappings[0]
        } : null
      });
      
      const transformed = this.transformTemplate(response.data);
      
      console.log(`[PropertyLevelService] Transformed template:`, {
        id: transformed.id,
        template_key: transformed.template_key,
        template_name: transformed.template_name,
        propertiesCount: transformed.properties?.length || 0,
        properties: transformed.properties?.map(p => ({
          id: p.id,
          property_key: p.property_key,
          has_id: !!p.id
        })) || [],
        properties_without_id: transformed.properties?.filter(p => !p.id).map(p => p.property_key) || []
      });
      
      console.log(`[PropertyLevelService] ===== getTemplate END =====`);
      
      return transformed;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching template ${templateIdentifier}:`, error);
      throw error;
    }
  }

  /**
   * Transform frontend template format to backend state template format
   */
  private static transformToBackendFormat(data: CreateTemplateRequest | UpdateTemplateRequest): any {
    // Cast to any to work around TypeScript union type narrowing issues
    const templateData = data as any;
    const backendData: any = {};
    
    // Always include name if provided (required for updates if being changed)
    if (templateData.template_name !== undefined && templateData.template_name !== null) {
      backendData.name = templateData.template_name;
    }
    
    // Include description if provided (send empty string as null)
    if (templateData.description !== undefined) {
      backendData.description = templateData.description || null;
    }
    
    // Include applies_to_artifact_types if artifact_type_hint is provided
    if (templateData.artifact_type_hint !== undefined) {
      if (templateData.artifact_type_hint) {
        backendData.applies_to_artifact_types = [templateData.artifact_type_hint];
      } else {
        backendData.applies_to_artifact_types = null;
      }
    }
    
    // Only include version, tags, states, transitions if explicitly provided in metadata
    // These are optional and should only be sent if they exist
    if (templateData.metadata) {
      if (templateData.metadata.version !== undefined && templateData.metadata.version !== null) {
        backendData.version = templateData.metadata.version;
      }
      if (templateData.metadata.tags !== undefined) {
        backendData.tags = templateData.metadata.tags && templateData.metadata.tags.length > 0 
          ? templateData.metadata.tags 
          : null;
      }
      if (templateData.metadata.states !== undefined) {
        backendData.states = templateData.metadata.states || [];
      }
      if (templateData.metadata.transitions !== undefined) {
        backendData.transitions = templateData.metadata.transitions || [];
      }
    }
    
    // Property mappings are managed separately via /property-mappings endpoint
    // Do NOT include them in template update/create requests
    // They are only used when creating a template with initial properties
    
    // Ensure we have at least one field to update (avoid empty object)
    if (Object.keys(backendData).length === 0) {
      console.warn('[PropertyLevelService] transformToBackendFormat: Empty request body, this might cause issues');
    }
    
    return backendData;
  }

  /**
   * Create a new property template
   */
  static async createTemplate(data: CreateTemplateRequest): Promise<GlobalPropertyTemplate> {
    try {
      const backendData = this.transformToBackendFormat(data);
      const response = await apiClient.post(API_ENDPOINTS.TEMPLATES, backendData);
      return this.transformTemplate(response.data);
    } catch (error: any) {
      console.error('[PropertyLevelService] Error creating template:', error);
      throw error;
    }
  }

  /**
   * Update a property template
   * @param templateIdentifier - Template UUID, name, or key
   * @param data - Update data
   */
  static async updateTemplate(templateIdentifier: string, data: UpdateTemplateRequest): Promise<GlobalPropertyTemplate> {
    try {
      // Resolve template identifier to UUID
      const templateId = await this.resolveTemplateId(templateIdentifier);
      const backendData = this.transformToBackendFormat(data);
      
      console.log(`[PropertyLevelService] ===== updateTemplate START =====`);
      console.log(`[PropertyLevelService] Template ID: ${templateId}`);
      console.log(`[PropertyLevelService] Request data:`, JSON.stringify(data, null, 2));
      console.log(`[PropertyLevelService] Backend request body:`, JSON.stringify(backendData, null, 2));
      console.log(`[PropertyLevelService] Backend request body keys:`, Object.keys(backendData));
      
      const response = await apiClient.put(API_ENDPOINTS.TEMPLATE(templateId), backendData);
      
      console.log(`[PropertyLevelService] Response status: ${response.status}`);
      console.log(`[PropertyLevelService] Response data:`, response.data);
      console.log(`[PropertyLevelService] ===== updateTemplate END =====`);
      
      return this.transformTemplate(response.data);
    } catch (error: any) {
      console.error(`[PropertyLevelService] ===== updateTemplate ERROR =====`);
      console.error(`[PropertyLevelService] Error updating template ${templateIdentifier}:`, error);
      if (error.response) {
        console.error(`[PropertyLevelService] Error response status:`, error.response.status);
        console.error(`[PropertyLevelService] Error response data:`, JSON.stringify(error.response.data, null, 2));
        console.error(`[PropertyLevelService] Error response headers:`, error.response.headers);
      }
      if (error.request) {
        console.error(`[PropertyLevelService] Request config:`, error.config);
        console.error(`[PropertyLevelService] Request URL:`, error.config?.url);
        console.error(`[PropertyLevelService] Request method:`, error.config?.method);
        console.error(`[PropertyLevelService] Request data:`, error.config?.data);
      }
      console.error(`[PropertyLevelService] ===== updateTemplate ERROR END =====`);
      throw error;
    }
  }

  /**
   * Delete a property template
   * @param templateIdentifier - Template UUID, name, or key
   */
  static async deleteTemplate(templateIdentifier: string): Promise<void> {
    try {
      // Resolve template identifier to UUID
      const templateId = await this.resolveTemplateId(templateIdentifier);
      await apiClient.delete(API_ENDPOINTS.TEMPLATE(templateId));
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error deleting template ${templateIdentifier}:`, error);
      throw error;
    }
  }

  /**
   * Add a property to a template
   * @param templateIdentifier - Template UUID, name, or key
   * @param data - Property mapping data
   */
  static async addPropertyToTemplate(
    templateIdentifier: string, 
    data: AddPropertyToTemplateRequest
  ): Promise<TemplatePropertyMapping> {
    try {
      // Validate required fields
      if (!data.property_key) {
        throw new Error('property_key is required');
      }
      if (!data.property_type) {
        throw new Error('property_type is required');
      }
      
      // Resolve template identifier to UUID
      const templateId = await this.resolveTemplateId(templateIdentifier);
      
      // Prepare request body with all fields that backend expects
      const requestBody: any = {
        property_key: data.property_key,
        property_type: data.property_type,
      };
      
      // Add optional fields if provided
      if (data.global_option_catalog_ref !== undefined) {
        requestBody.global_option_catalog_ref = data.global_option_catalog_ref;
      }
      if (data.global_property_ref !== undefined) {
        // Backend also accepts global_property_key for new way
        requestBody.global_property_key = data.global_property_ref;
      }
      if (data.created_in_states !== undefined && data.created_in_states.length > 0) {
        requestBody.created_in_states = data.created_in_states;
      }
      if (data.updated_in_states !== undefined && data.updated_in_states.length > 0) {
        requestBody.updated_in_states = data.updated_in_states;
      }
      if (data.referenced_artifact_type_ids !== undefined && data.referenced_artifact_type_ids.length > 0) {
        requestBody.referenced_artifact_type_ids = data.referenced_artifact_type_ids;
      }
      
      // Use correct endpoint: /property-mappings instead of /properties
      console.log(`[PropertyLevelService] ===== addPropertyToTemplate START =====`);
      console.log(`[PropertyLevelService] Template ID: ${templateId}`);
      console.log(`[PropertyLevelService] Request body:`, requestBody);
      
      const response = await apiClient.post(
        API_ENDPOINTS.TEMPLATE_PROPERTY_MAPPINGS(templateId), 
        requestBody
      );
      
      console.log(`[PropertyLevelService] Response status: ${response.status}`);
      console.log(`[PropertyLevelService] Response data:`, response.data);
      console.log(`[PropertyLevelService] ===== addPropertyToTemplate END =====`);
      
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error adding property to template ${templateIdentifier}:`, error);
      throw error;
    }
  }

  /**
   * Remove a property from a template
   * @param templateIdentifier - Template UUID, name, or key (kept for compatibility but not used in endpoint)
   * @param mappingId - Property mapping ID (UUID) to remove
   */
  static async removePropertyFromTemplate(
    templateIdentifier: string, 
    mappingId: string
  ): Promise<void> {
    try {
      console.log(`[PropertyLevelService] ===== removePropertyFromTemplate START =====`);
      console.log(`[PropertyLevelService] Template ID: ${templateIdentifier}, Mapping ID: ${mappingId}`);
      
      // Production backend uses DELETE /template-property-mappings/{mapping_id}
      // Note: template_id is NOT included in the DELETE endpoint path
      const endpoint = `/template-property-mappings/${mappingId}`;
      console.log(`[PropertyLevelService] DELETE endpoint: ${endpoint}`);
      
      await apiClient.delete(endpoint);
      
      console.log(`[PropertyLevelService] ===== removePropertyFromTemplate END =====`);
    } catch (error: any) {
      console.error(`[PropertyLevelService] ===== removePropertyFromTemplate ERROR =====`);
      console.error(`[PropertyLevelService] Error removing mapping ${mappingId} from template ${templateIdentifier}:`, error);
      if (error.response) {
        console.error(`[PropertyLevelService] Error response status:`, error.response.status);
        console.error(`[PropertyLevelService] Error response data:`, JSON.stringify(error.response.data, null, 2));
      }
      console.error(`[PropertyLevelService] ===== removePropertyFromTemplate ERROR END =====`);
      throw error;
    }
  }

  /**
   * Update property order in template
   * @param templateIdentifier - Template UUID, name, or key
   * @param propertyOrders - Array of property orders
   */
  static async updateTemplatePropertyOrder(
    templateIdentifier: string, 
    propertyOrders: Array<{ property_key: string; display_order: number }>
  ): Promise<void> {
    try {
      // Resolve template identifier to UUID
      const templateId = await this.resolveTemplateId(templateIdentifier);
      
      // Use correct endpoint path
      await apiClient.put(`${API_ENDPOINTS.TEMPLATE_PROPERTY_MAPPINGS(templateId)}/order`, {
        property_orders: propertyOrders
      });
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error updating property order in template ${templateIdentifier}:`, error);
      throw error;
    }
  }

  /**
   * Apply a template to a prime artifact
   */
  static async applyTemplateToArtifact(
    templateKey: string, 
    primeArtifactId: string
  ): Promise<ApplyTemplateResult> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.APPLY_TEMPLATE(templateKey, primeArtifactId));
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error applying template ${templateKey} to artifact ${primeArtifactId}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // Level 3: Prime Artifact Properties
  // ============================================================================

  /**
   * Get all properties for a prime artifact
   */
  static async getPrimeProperties(primeArtifactId: string): Promise<PrimeArtifactProperty[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PRIME_PROPERTIES(primeArtifactId));
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return response.data.properties || response.data.items || [];
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching prime properties for ${primeArtifactId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific prime artifact property
   */
  static async getPrimeProperty(
    primeArtifactId: string, 
    propertyKey: string
  ): Promise<PrimeArtifactProperty> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.PRIME_PROPERTY(primeArtifactId, propertyKey));
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching prime property ${propertyKey}:`, error);
      throw error;
    }
  }

  /**
   * Update a prime artifact property
   */
  static async updatePrimeProperty(
    primeArtifactId: string,
    propertyKey: string,
    data: UpdatePrimePropertyRequest
  ): Promise<PrimeArtifactProperty> {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.PRIME_PROPERTY(primeArtifactId, propertyKey), 
        data
      );
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error updating prime property ${propertyKey}:`, error);
      throw error;
    }
  }

  /**
   * Get inheritance info for a prime property
   */
  static async getPrimePropertyInheritance(
    primeArtifactId: string, 
    propertyKey: string
  ): Promise<{ global: GlobalPropertyDefinition | null; customized_fields: string[] }> {
    try {
      const response = await apiClient.get(
        `${API_ENDPOINTS.PRIME_PROPERTY(primeArtifactId, propertyKey)}/inheritance`
      );
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching inheritance for ${propertyKey}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // Level 4: Company Artifact Properties
  // ============================================================================

  /**
   * Get all properties for a company artifact
   */
  static async getCompanyProperties(artifactId: string): Promise<CompanyArtifactProperty[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COMPANY_PROPERTIES(artifactId));
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return response.data.properties || response.data.items || [];
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching company properties for ${artifactId}:`, error);
      throw error;
    }
  }

  /**
   * Get resolved property configuration (full inheritance chain)
   */
  static async getResolvedPropertyConfig(
    artifactId: string, 
    propertyKey: string
  ): Promise<ResolvedPropertyConfig> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.RESOLVED_CONFIG(artifactId, propertyKey));
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching resolved config for ${propertyKey}:`, error);
      throw error;
    }
  }

  /**
   * Get all resolved property configs for a company artifact
   */
  static async getAllResolvedConfigs(artifactId: string): Promise<ResolvedPropertyConfig[]> {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.COMPANY_PROPERTIES(artifactId)}/resolved`);
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return response.data.configs || response.data.items || [];
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching all resolved configs for ${artifactId}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // Level 5: Artifact Item Properties
  // ============================================================================

  /**
   * Get all property values for an artifact item
   */
  static async getArtifactItemProperties(itemId: string): Promise<ArtifactItemProperties> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ARTIFACT_ITEM_PROPERTIES(itemId));
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching item properties for ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Update property values for an artifact item
   */
  static async updateArtifactItemProperties(
    itemId: string, 
    data: UpdateArtifactItemPropertiesRequest
  ): Promise<ArtifactItemProperties> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.ARTIFACT_ITEM_PROPERTIES(itemId), data);
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error updating item properties for ${itemId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific property value for an artifact item
   */
  static async getArtifactItemProperty(itemId: string, propertyKey: string): Promise<any> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ARTIFACT_ITEM_PROPERTY(itemId, propertyKey));
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching item property ${propertyKey}:`, error);
      throw error;
    }
  }

  /**
   * Update a specific property value for an artifact item
   */
  static async updateArtifactItemProperty(
    itemId: string, 
    propertyKey: string, 
    value: any
  ): Promise<any> {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.ARTIFACT_ITEM_PROPERTY(itemId, propertyKey), 
        { value }
      );
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error updating item property ${propertyKey}:`, error);
      throw error;
    }
  }

  /**
   * Recalculate a system-generated property
   */
  static async recalculateProperty(
    itemId: string, 
    propertyKey: string
  ): Promise<RecalculatePropertyResponse> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.RECALCULATE_PROPERTY(itemId, propertyKey));
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error recalculating property ${propertyKey}:`, error);
      throw error;
    }
  }

  /**
   * Recalculate all system-generated properties for an artifact item
   */
  static async recalculateAllProperties(itemId: string): Promise<RecalculatePropertyResponse[]> {
    try {
      const response = await apiClient.post(
        `${API_ENDPOINTS.ARTIFACT_ITEM_PROPERTIES(itemId)}/recalculate-all`
      );
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error recalculating all properties for ${itemId}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Get available templates for a prime artifact type
   */
  static async getTemplatesForArtifactType(artifactTypeHint: string): Promise<GlobalPropertyTemplate[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.TEMPLATES, {
        params: { artifact_type_hint: artifactTypeHint }
      });
      
      if (Array.isArray(response.data)) {
        return response.data.map((t: any) => this.transformTemplate(t));
      }
      
      return (response.data.items || []).map((t: any) => this.transformTemplate(t));
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error fetching templates for ${artifactTypeHint}:`, error);
      throw error;
    }
  }

  /**
   * Validate property configuration
   */
  static async validatePropertyConfig(config: Partial<GlobalPropertyDefinition>): Promise<{
    valid: boolean;
    errors?: string[];
  }> {
    try {
      const response = await apiClient.post('/properties/validate', config);
      return response.data;
    } catch (error: any) {
      console.error('[PropertyLevelService] Error validating property config:', error);
      throw error;
    }
  }

  /**
   * Preview resolved configuration for a prime property
   */
  static async previewResolvedConfig(
    primeArtifactId: string,
    propertyKey: string
  ): Promise<ResolvedPropertyConfig> {
    try {
      const response = await apiClient.get(
        `${API_ENDPOINTS.PRIME_PROPERTY(primeArtifactId, propertyKey)}/preview-resolved`
      );
      return response.data;
    } catch (error: any) {
      console.error(`[PropertyLevelService] Error previewing resolved config for ${propertyKey}:`, error);
      throw error;
    }
  }
}

export default PropertyLevelService;
